import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/auth.store';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || undefined;
  const { register, isLoading } = useAuthStore();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Пароли не совпадают');
      return;
    }

    if (!acceptTerms) {
      toast.error('Необходимо согласие с офертой и политикой обработки персональных данных');
      return;
    }

    try {
      await register({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        acceptTerms: true,
        referralCode,
      });
      navigate('/verify-email');
    } catch (error) {
      // Error handled in store
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Регистрация
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 1,
              mb: 2,
              p: 1.5,
              borderRadius: 3,
              color: '#fff',
              background: 'linear-gradient(135deg, #5468ff 0%, #9b6bff 100%)',
              boxShadow: '0 6px 18px rgba(84,104,255,0.35)',
            }}
          >
            <CardGiftcardIcon />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Акция: 7 дней тарифа «Профи» — бесплатно
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Включится сразу после подтверждения email. Банковская карта не требуется.
              </Typography>
            </Box>
          </Box>

          {referralCode && (
            <Typography variant="body2" align="center" color="success.main" sx={{ mb: 1 }}>
              Вы регистрируетесь по приглашению (код {referralCode})
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
              helperText="Только российская почта: @mail.ru, @yandex.ru, @rambler.ru и др. (требование законодательства РФ)"
            />
            <TextField
              fullWidth
              label="Имя пользователя"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Пароль"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
              required
              helperText="Минимум 8 символов, буквы и цифры"
            />
            <TextField
              fullWidth
              label="Подтвердите пароль"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              margin="normal"
              required
            />
            <FormControlLabel
              sx={{ mt: 1, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  sx={{ mt: -1 }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Принимаю условия{' '}
                  <a href="/vkbp/docs/oferta.html" target="_blank" rel="noopener noreferrer">
                    публичной оферты
                  </a>{' '}
                  и даю согласие на обработку персональных данных в соответствии с{' '}
                  <a href="/vkbp/docs/privacy.html" target="_blank" rel="noopener noreferrer">
                    политикой
                  </a>
                </Typography>
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !acceptTerms}
              sx={{ mt: 2, mb: 2 }}
            >
              {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Уже есть аккаунт?{' '}
              <Link to="/login" style={{ textDecoration: 'none' }}>
                Войти
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
